import React, { useState, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import { useStyles } from "./styles";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOut as gpioIcon } from "@fortawesome/pro-light-svg-icons";
import { ROUTINE_TYPE } from "../../../../constants/index";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ActionForm from "./ActionForm";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import { useCoreEntityContext } from "../../../../hooks/useCoreEntitySlice";
import { selectContextEntity } from "../../../../state/slices/CoreEntity";
import apiClient from "../../../../auth/apiClient";
import GPIOService from "../../../../services/GPIOService";

export const GpioOutputsActionFormAddEditSchema = Yup.object().shape({
  Device: Yup.string().required("Required"),
  Output: Yup.number()
    .moreThan(0, "Required")
    .required("Required"),
  State: Yup.string().required("Required"),
});

const GpioOutputsActionForm = ({ handleSave, handleCancel, action }) => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const [entities, setEntities] = useState([]);
  const [selectedDeviceValue, setSelectedDeviceValue] = useState("");
  const useCoreEntitySlice = useCoreEntityContext();

  const gpioService = new GPIOService(apiClient);
  const devicesInFacility = useSelector(
    (state) =>
      _.orderBy(
        useCoreEntitySlice
          ? selectContextEntity(state).children
          : state.entities.Context.children,
        ["name"],
        ["asc"]
      ),
    shallowEqual
  );

  const GetDevices = () => {
    if (Array.isArray(devicesInFacility)) {
      var devices = devicesInFacility?.map((device) => {
        return { name: device?.name, value: device?.entityid };
      });
      setEntities(devices);
    }
  };

  const formatAndSave = (values) => {
    const selectedOutput = gpioMappings.find(
      (mapping) => mapping.ioMappingID === values.Output
    );
    const switchGpio = {
      payload: {
        DeviceId: values.Device,
        OutputMappingId: values.Output,
        OutputFriendlyName: selectedOutput?.friendlyName || "",
        State: values.State,
      },
      actionTypeID: ROUTINE_TYPE.GPIO,
      order: action?.order ?? 0,
    };
    handleSave(switchGpio);
  };

  const defaults = {
    Device: action?.payload?.DeviceId ?? "",
    Output: action?.payload?.OutputMappingId ?? 0,
    State: action?.payload?.State ?? "",
  };

  const GPIO_State_OPTIONS = [
    { value: 1, label: "Active" },
    { value: 2, label: "Inactive" },
  ];

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: defaults,
    resolver: yupResolver(GpioOutputsActionFormAddEditSchema),
  });

  const defaultIOMapping = {
    friendlyName: "",
    ioMappingID: 0,
  };
  const defaultIOMappingsArray = [defaultIOMapping];
  const [gpioMappings, setGPIOMappings] = useState(defaultIOMappingsArray);

  const GetGPIOMappingInfo = async (deviceValue) => {
    try {
      var result = await gpioService.getIOMappings(deviceValue);
      const outputMappings = result.data.filter(
        (item) => item.gpioType?.toLowerCase() === "output"
      );
      setGPIOMappings(
        outputMappings.length > 0 ? outputMappings : defaultIOMappingsArray
      );

      if (outputMappings.length === 0) {
        setValue("Output", 0);
      }
    } catch (error) {
      setGPIOMappings(defaultIOMappingsArray);
      enqueueSnackbar("Failed to get GPIOMappings", {
        variant: "error",
        tag: "getGPIOError",
      });
      setValue("Output", 0);
    }
  };

  useEffect(() => {
    GetDevices();
    if (selectedDeviceValue) {
      GetGPIOMappingInfo(selectedDeviceValue);
    }
    if (defaults.Device && selectedDeviceValue =="") {
      GetGPIOMappingInfo(defaults.Device);
    }
  }, [defaults.Device, selectedDeviceValue]);

  return (
    <>
      <ActionForm
        handleSave={formatAndSave}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        icon={<FontAwesomeIcon icon={gpioIcon} />}
        edit={action}
        primaryText={"GPIO"}
        secondaryText={"Outputs"}
      >
        <Grid container className={clsx([classes.drawerContainer])}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Controller
              name="Device"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="outlined">
                  <Grid container>
                    <Grid container item xs={12} sm={4} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        id="Device"
                        className={clsx([classes.deviceLabel])}
                      >
                        Device
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        data-id={"deviceSelect"}
                        data-testid={"deviceSelect"}
                        value={field.value || []}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                          setSelectedDeviceValue(event.target.value);
                          setValue("Output", 0);
                        }}
                        className={clsx([classes.deviceDropdown])}
                        variant="outlined"
                        inputProps={{
                          id: "device-dropdown",
                        }}
                        error={!!errors.Device}
                      >
                        {entities.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Device && (
                        <div className={clsx([classes.selectError])}>
                          {errors.Device.message}
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Controller
              name="Output"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth FormLabel="Output" variant="outlined">
                  <Grid container>
                    <Grid container item xs={12} sm={4} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        id="Output"
                        className={clsx([classes.outputLabel])}
                      >
                        Output
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        data-id={"outputSelect"}
                        data-testid={"outputSelect"}
                        data-value={field.value || []}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                        }}
                        className={clsx([classes.outputDropdown])}
                        variant="outlined"
                        inputProps={{
                          id: "output-dropdown",
                        }}
                        error={!!errors.Output}
                        helperText={errors.Output && errors.Output.message}
                      >
                        {gpioMappings.map((item) => (
                          <MenuItem
                            key={item.ioMappingID}
                            value={item.ioMappingID}
                          >
                            {item.friendlyName}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Output && (
                        <div className={clsx([classes.selectError])}>
                          {errors.Output.message}
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Controller
              name="State"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth FormLabel="State" variant="outlined">
                  <Grid container>
                    <Grid container item xs={12} sm={4} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        id="State"
                        className={clsx([classes.stateLabel])}
                      >
                        State
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        data-id={"stateSelect"}
                        data-testid={"stateSelect"}
                        data-value={field?.value}
                        className={clsx([classes.stateDropdown])}
                        variant="outlined"
                        inputProps={{
                          id: "state-dropdown",
                        }}
                        error={!!errors.State}
                        helperText={errors.State && errors.State.message}
                      >
                        {GPIO_State_OPTIONS.map((state) => (
                          <MenuItem key={state.value} value={state.label}>
                            {state.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.State && (
                        <div className={clsx([classes.selectError])}>
                          {errors.State.message}
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </ActionForm>
    </>
  );
};
export default GpioOutputsActionForm;
